// Weather for each catch (Open-Meteo: the forecast service for recent catches, the archive for older ones),
// the moon phase, and filling in weather and water temperature for catches that are missing them.
import type { Catch, Weather } from "../shared/model.ts";
import { dbGet, dbPut } from "./db.ts";
import { list, nameOf } from "./i18n.ts";
import { upgrade } from "./species.ts";
import { catches, render } from "./state.ts";
import { isoDay } from "./util.ts";
import { needsWater, updateWater } from "./water.ts";

const HOURLY = "temperature_2m,relative_humidity_2m,precipitation,cloud_cover,pressure_msl,wind_speed_10m,wind_direction_10m,wind_gusts_10m,weather_code";
type HourlyKey = "temperature_2m" | "relative_humidity_2m" | "precipitation" | "cloud_cover" | "pressure_msl" | "wind_speed_10m" | "wind_direction_10m" | "wind_gusts_10m" | "weather_code";

/** The weather at the hour nearest `time`. */
export async function fetchWeather(lat: number, lon: number, time: number): Promise<Weather> {
  const ageDays = (Date.now() - time) / 864e5;
  const base = ageDays > 60 ? "https://archive-api.open-meteo.com/v1/archive" : "https://api.open-meteo.com/v1/forecast";
  const q = new URLSearchParams({ latitude: lat.toFixed(4), longitude: lon.toFixed(4), hourly: HOURLY, timezone: "GMT",
    wind_speed_unit: "ms", start_date: isoDay(time - 4 * 36e5), end_date: isoDay(time + 36e5) });
  const res = await fetch(base + "?" + q);
  if (!res.ok) throw new Error("weather " + res.status);
  const h = (await res.json()).hourly as ({ time: string[] } & Record<HourlyKey, (number | null)[]>) | undefined;
  if (!h?.time?.length) throw new Error("no weather data");
  let best = 0, bestD = Infinity;
  h.time.forEach((s, i) => { const d = Math.abs(Date.parse(s + ":00Z") - time); if (d < bestD) { bestD = d; best = i; } });
  const v = (k: HourlyKey, i = best) => h[k]?.[i] ?? null;
  const p3 = best >= 3 ? v("pressure_msl", best - 3) : null;
  const w: Weather = { status: "ok", temp: v("temperature_2m"), humidity: v("relative_humidity_2m"), precip: v("precipitation"),
    cloud: v("cloud_cover"), pressure: v("pressure_msl"), wind: v("wind_speed_10m"), windDir: v("wind_direction_10m"),
    gust: v("wind_gusts_10m"), code: v("weather_code"), source: "Open-Meteo", forLat: lat, forLon: lon, forTime: time };
  if (w.pressure != null && p3 != null) w.pressure3h = Math.round((w.pressure - p3) * 10) / 10;
  if (w.temp == null && w.pressure == null) throw new Error("empty weather");
  return w;
}

/** "falling" / "rising" / "steady" for a 3-hour pressure change. */
export function pressureTrend(d: number | null | undefined): string {
  if (d == null) return "";
  return nameOf("trend", d <= -1 ? "falling" : d >= 1 ? "rising" : "steady") ?? "";
}

/** Moon phase name and how much of it is lit, %. */
export function moon(ts: number): { name: string; illum: number } {
  const P = 29.530588853, ref = Date.UTC(2000, 0, 6, 18, 14); // a known new moon
  const age = ((((ts - ref) / 864e5) % P) + P) % P, f = age / P;
  const illum = Math.round((1 - Math.cos(2 * Math.PI * f)) / 2 * 100);
  return { name: list("moonPhases")[Math.round(f * 8) % 8], illum };
}

/** True when the catch has a place but no weather yet, or the weather is for another place or time. */
export function needsWeather(c: Catch): boolean {
  if (!c.loc) return false;
  const w = c.weather;
  if (!w || w.status !== "ok") return true;
  return Math.abs(w.forLat - c.loc.lat) > 0.01 || Math.abs(w.forLon - c.loc.lon) > 0.01 || Math.abs(w.forTime - c.time) > 30 * 6e4;
}

let busy = false;
/** Looks up weather and water temperature for every catch that needs them (when online). */
export async function refreshWeather(): Promise<void> {
  if (busy || !navigator.onLine) return;
  busy = true;
  try {
    for (const c of catches.filter(needsWeather)) {
      try {
        const w = await fetchWeather(c.loc!.lat, c.loc!.lon, c.time);
        const fresh = await dbGet("catches", c.id);
        if (!fresh) continue;
        fresh.weather = w;
        await dbPut("catches", fresh);
        Object.assign(c, upgrade(fresh));
      } catch (e) { console.warn("Weather lookup failed", e); }
    }
    for (const c of catches.filter(needsWater)) {
      try { await updateWater(c); } catch (e) { console.warn("Water temperature lookup failed", e); }
    }
  } finally { busy = false; render(); }
}
addEventListener("online", () => void refreshWeather());
